/**
 * DiagramWrapper
 *
 * 1. Isolates diagram CSS by scoping all <style> tags to the diagram's
 *    unique container ID at mount time.
 * 2. Adds a click-to-expand lightbox so readers can view diagrams full-screen.
 */
import { useRef, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

// ── CSS scoping helpers ───────────────────────────────────────────────────────

function scopeCSS(css: string, scopeId: string): string {
  const selector = `#${scopeId}`;
  css = css.replace(/\/\*[\s\S]*?\*\//g, "");

  const keyframeBlocks: string[] = [];
  css = css.replace(/@keyframes\s+[\s\S]*?\{[\s\S]*?\}\s*\}/g, (match) => {
    const idx = keyframeBlocks.push(match) - 1;
    return `/*__kf${idx}__*/`;
  });

  css = css.replace(/@media[^{]+\{([\s\S]*?)\}\s*\}/g, (match, inner) => {
    const header = match.slice(0, match.indexOf("{") + 1);
    return `${header}${scopeRules(inner, selector)}}`;
  });

  css = scopeRules(css, selector);

  keyframeBlocks.forEach((kf, i) => {
    css = css.replace(`/*__kf${i}__*/`, kf);
  });

  return css;
}

function scopeRules(css: string, selector: string): string {
  return css.replace(/([^{}@]+)\{([^{}]*)\}/g, (_, rawSelectors, declarations) => {
    const scopedSelectors = rawSelectors
      .split(",")
      .map((s: string) => {
        const trimmed = s.trim();
        if (!trimmed) return "";
        if (/^(html|body|:root)$/.test(trimmed)) return selector;
        return `${selector} ${trimmed}`;
      })
      .filter(Boolean)
      .join(", ");
    return scopedSelectors ? `${scopedSelectors} { ${declarations} }` : "";
  });
}

// ── Lightbox ─────────────────────────────────────────────────────────────────

function Lightbox({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    // Prevent body scroll
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        backdropFilter: "blur(4px)",
        animation: "dgm-fade-in 0.15s ease",
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close diagram"
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff",
          fontSize: "20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
          zIndex: 10000,
        }}
      >
        ×
      </button>

      {/* Diagram content — stop click propagation so clicking diagram doesn't close */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "1000px",
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: "16px",
          animation: "dgm-scale-in 0.15s ease",
        }}
      >
        {children}
      </div>

      <style>{`
        @keyframes dgm-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes dgm-scale-in { from { transform: scale(0.95) } to { transform: scale(1) } }
      `}</style>
    </div>,
    document.body
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface DiagramWrapperProps {
  children: ReactNode;
  slug: string;
}

export function DiagramWrapper({ children, slug }: DiagramWrapperProps) {
  // Slug-derived rather than useId — see CaseStudyChart: a counter-based id
  // differs between the prerendered HTML and the client that hydrates it.
  const id = `dgm-${slug}`;
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    container.querySelectorAll<HTMLStyleElement>("style").forEach((styleTag) => {
      const original = styleTag.textContent ?? "";
      if (original.includes(`#${id}`)) return;
      styleTag.textContent = scopeCSS(original, id);
    });
  }, [id]);

  return (
    <>
      {/* Inline diagram — clickable */}
      <div
        id={id}
        ref={ref}
        onClick={() => setOpen(true)}
        title="Click to expand"
        style={{
          margin: "48px 0",
          cursor: "zoom-in",
          borderRadius: "12px",
          transition: "box-shadow 0.2s, transform 0.2s",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.boxShadow = "0 0 0 2px rgba(87,215,226,0.5)";
          (e.currentTarget as HTMLDivElement).style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
          (e.currentTarget as HTMLDivElement).style.transform = "none";
        }}
      >
        {children}

        {/* Expand hint badge */}
        <div style={{
          position: "absolute",
          bottom: "12px",
          right: "12px",
          background: "rgba(0,0,0,0.6)",
          border: "1px solid rgba(87,215,226,0.3)",
          borderRadius: "6px",
          padding: "4px 10px",
          fontSize: "11px",
          color: "rgba(255,255,255,0.5)",
          pointerEvents: "none",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          backdropFilter: "blur(4px)",
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
          </svg>
          Click to expand
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <Lightbox onClose={() => setOpen(false)}>
          <div id={`${id}-modal`}>
            {children}
          </div>
        </Lightbox>
      )}
    </>
  );
}
